import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth.js';
import api from '../services/auth.service.js';
import type { Lead, LeadFormInput, PipelineEtapa } from '@azoupvendas/shared';

// AIDEV-NOTE: Hook de leads migrado de Supabase para REST API local (pg pool).
// Mapeamento de colunas feito no servidor: titulo→nome, pipeline_etapa→estagio, valor→valor_estimado.

export const LEADS_QUERY_KEY = 'leads';

export function useLeads() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [LEADS_QUERY_KEY, user?.client_id, user?.vendedor_id],
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<Lead[]> => {
      const res = await api.get<{ leads: Lead[] }>('/leads');
      return res.data.leads;
    }
  });

  // AIDEV-NOTE: Leads agrupados por etapa para renderização do Kanban
  const byEtapa = (query.data ?? []).reduce(
    (acc, lead) => {
      const etapa = lead.estagio;
      if (!acc[etapa]) acc[etapa] = [];
      acc[etapa].push(lead);
      return acc;
    },
    {} as Record<PipelineEtapa, Lead[]>
  );

  const createLead = useMutation({
    mutationFn: async (input: LeadFormInput) => {
      const res = await api.post<{ lead: Lead }>('/leads', input);
      return res.data.lead;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LEADS_QUERY_KEY] })
  });

  // AIDEV-NOTE: Optimistic update — atualiza cache imediatamente antes da confirmação do servidor
  const updateEtapa = useMutation({
    mutationFn: async ({ id, etapa }: { id: string; etapa: PipelineEtapa }) => {
      const res = await api.patch<{ lead: Lead }>(`/leads/${id}/etapa`, { etapa });
      return res.data.lead;
    },
    onMutate: async ({ id, etapa }) => {
      await queryClient.cancelQueries({ queryKey: [LEADS_QUERY_KEY] });
      const prev = queryClient.getQueryData<Lead[]>([
        LEADS_QUERY_KEY,
        user?.client_id,
        user?.vendedor_id
      ]);
      queryClient.setQueryData<Lead[]>(
        [LEADS_QUERY_KEY, user?.client_id, user?.vendedor_id],
        (old) => old?.map((l) => (l.id === id ? { ...l, estagio: etapa } : l)) ?? []
      );
      return { prev };
    },
    // AIDEV-NOTE: Rollback automático em erro — restaura estado anterior do cache
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(
          [LEADS_QUERY_KEY, user?.client_id, user?.vendedor_id],
          context.prev
        );
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: [LEADS_QUERY_KEY] })
  });

  const updateLead = useMutation({
    mutationFn: async ({ id, ...input }: Partial<LeadFormInput> & { id: string }) => {
      const res = await api.patch<{ lead: Lead }>(`/leads/${id}`, input);
      return res.data.lead;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LEADS_QUERY_KEY] })
  });

  return {
    leads: query.data ?? [],
    byEtapa,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,
    createLead,
    updateEtapa,
    updateLead
  };
}
